import { useMemo, useState } from "react"

import { Autocomplete, TextField, InputAdornment, Stack, Typography, CircularProgress, SvgIcon } from "@mui/material"
import { SearchLineIcon } from "@equipmentshare/ds2-icons"

import { useBulkPartsQuery } from "@app/hooks/useBulkPartsQuery"
import { BulkPart } from "@app/services/types"

type BulkPartPickerProps = {
	placeholder?: string
	disabled?: boolean
	minChars?: number
	debounceMs?: number
	field?: any
	error?: boolean
	helperText?: string
}

export type BulkPartPickerOption = {
	label: string
	id: number
	value: BulkPart
}

export const BulkPartPicker: React.FC<BulkPartPickerProps> = ({
	placeholder = "Search Bulk...",
	field,
	helperText,
	disabled,
	debounceMs,
	minChars = 3,
	error = false,
}) => {
	const [search, setSearch] = useState<string>("")

	const { data: bulkPartResponse, isLoading } = useBulkPartsQuery({
		query: search,
		minChars,
		disabled,
		debounceMs,
	})

	const options = useMemo<BulkPartPickerOption[]>(() => {
		const bulkParts = bulkPartResponse?.bulk ?? []
		return bulkParts.map((bp) => ({ label: `${bp.search}`, id: bp.part_id, value: bp }))
	}, [bulkPartResponse])

	const handleChange = (_: React.SyntheticEvent, selectedBulkPart: BulkPartPickerOption | null) => {
		// Set the full option object as the value
		field?.onChange(selectedBulkPart)
	}

	return (
		<Stack>
			<Typography variant="subtitle2" fontWeight="bold" sx={{ marginBottom: "4px" }}>
				Bulk Cat Class ID
			</Typography>
			<Autocomplete
				options={options}
				value={field?.value ?? null}
				loading={isLoading}
				loadingText="Searching..."
				inputValue={search}
				onInputChange={(_, newInputValue) => {
					setSearch(newInputValue)
				}}
				popupIcon={null}
				disabled={disabled}
				autoComplete
				noOptionsText={search.trim().length < minChars ? `Type ${minChars} or more characters` : "No results"}
				onChange={handleChange}
				isOptionEqualToValue={(a, b) => a.id === b.id}
				renderInput={(params) => (
					<TextField
						{...params}
						size="small"
						placeholder={placeholder}
						error={error}
						helperText={helperText}
						slotProps={{
							inputLabel: { sx: { fontWeight: "bold", color: "black" } },
							input: {
								...params.InputProps,
								endAdornment: (
									<>
										{isLoading ? (
											<InputAdornment position="end">
												<CircularProgress size={18} />
											</InputAdornment>
										) : null}
										{params.InputProps?.endAdornment}
									</>
								),
								startAdornment: (
									<InputAdornment position="start">
										<SvgIcon
											component={SearchLineIcon}
											sx={{ color: (theme) => theme.palette.grey[900] }}
										/>
									</InputAdornment>
								),
							},
							htmlInput: params.inputProps,
						}}
					/>
				)}
				sx={{ width: "100%" }}
			/>
		</Stack>
	)
}
export default BulkPartPicker
